import { useState } from "react";
import { toast } from "sonner";
import { useApi, useResource } from "@/lib/api";
import { PageHeader, Panel, StatusPill, Loading, ErrorNote, fmtTime } from "@/components/kit";

const input = "rounded-sm border border-[var(--cc-line)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--cc-navy)]";

const Request = ({ onDone }) => {
  const call = useApi();
  const [f, setF] = useState({ title: "", scope: "development" });
  const submit = async (e) => {
    e.preventDefault();
    try { await call("/approvals", { method: "POST", body: f }); toast.success("Approval requested"); setF({ ...f, title: "" }); onDone(); }
    catch (err) { toast.error(err.message); }
  };
  return (
    <form onSubmit={submit} className="grid gap-3 md:grid-cols-[1fr_180px_auto]" data-testid="approval-request-form">
      <input data-testid="approval-title-input" className={input} placeholder="What needs approval?" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <select data-testid="approval-scope-select" className={input} value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })}>
        {["development", "qa", "release", "production"].map((s) => <option key={s}>{s}</option>)}
      </select>
      <button data-testid="approval-submit-button" className="rounded-sm bg-[var(--cc-navy)] px-5 py-2 text-sm text-white hover:bg-[var(--cc-signal)] hover:text-[var(--cc-navy)]">Request</button>
    </form>
  );
};

const Item = ({ a, onDone }) => {
  const call = useApi();
  const [note, setNote] = useState("");
  const decide = async (decision) => {
    try { await call(`/approvals/${a.id}/decision`, { method: "POST", body: { decision, note } }); toast.success(decision === "approve" ? "Approval granted" : "Approval rejected"); onDone(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="py-5" data-testid={`approval-row-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-xs text-[var(--cc-muted)]">{a.id} · scope {a.scope}</p>
          <p className="mt-1 font-medium text-[var(--cc-navy)]">{a.title}</p>
          <p className="text-xs text-[var(--cc-muted)]">Requested by {a.requested_by}</p>
        </div>
        <StatusPill status={a.status} testId={`approval-status-${a.id}`} />
      </div>
      {a.status === "pending" && (
        <div className="mt-3 flex flex-wrap gap-2">
          <input data-testid={`approval-note-${a.id}`} className={`${input} min-w-0 flex-1`} placeholder="Decision note" value={note} onChange={(e) => setNote(e.target.value)} />
          <button data-testid={`approval-approve-${a.id}`} onClick={() => decide("approve")} className="rounded-sm bg-[var(--cc-ok)] px-4 py-2 text-xs text-white hover:opacity-90">Approve</button>
          <button data-testid={`approval-reject-${a.id}`} onClick={() => decide("reject")} className="rounded-sm bg-[var(--cc-deny)] px-4 py-2 text-xs text-white hover:opacity-90">Reject</button>
        </div>
      )}
      {a.history?.map((h, i) => <p key={i} className="mt-2 font-mono text-[11px] text-[var(--cc-muted)]">↳ {h.decision} by {h.by} · {fmtTime(h.at)} · audit #{h.audit_seq} {h.note && `· "${h.note}"`}</p>)}
    </div>
  );
};

export default function Approvals() {
  const { data, error, loading, reload } = useResource("/approvals");
  return (
    <div>
      <PageHeader eyebrow="Governance · Product Owner" title="Approval Queue" />
      <Panel title="Request approval" className="mb-6"><Request onDone={reload} />
        <p className="mt-3 text-xs text-[var(--cc-muted)]">Rules enforced server-side: no actor can approve its own request; Production approvals cannot be granted in Phase 1.</p></Panel>
      <Panel title="Queue & history" testId="approval-queue">{loading ? <Loading /> : error ? <ErrorNote error={error} /> :
        <div className="divide-y divide-[var(--cc-line)]">{data.map((a) => <Item key={a.id} a={a} onDone={reload} />)}</div>}</Panel>
    </div>
  );
}
